import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { useMediaQuery, useTheme } from "@mui/material";
import CheckItem from "./CheckItem";
import { getStorageValue, useLocalStorage } from "../../hooks/useLocalStorage";
import { useEffect, useState } from "react";

function updateGTAG(hasAnalytics: boolean) {
  (window as any).gtag("consent", "update", {
    ad_storage: hasAnalytics ? "granted" : "denied",
    analytics_storage: hasAnalytics ? "granted" : "denied",
  });
}

interface Props {
  open: boolean;
  handleClose: (hasFinished?: boolean) => void;
}

const ManageCookiesDialog: React.FC<Props> = ({ open, handleClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [analytics, setAnalytics] = useState<boolean>(false);
  const [preferences, setPreferences] = useState<boolean>(false);

  const [, setPreferencesStorage] = useLocalStorage("cookiesPreferences", "");
  const [, setAnalyticsStorage] = useLocalStorage("cookiesAnalytics", "");
  const [, setSeenCookieSettings] = useLocalStorage("seenCookieSettings", "");

  function handleAcceptAll() {
    setPreferencesStorage("true");
    setAnalyticsStorage("true");
    updateGTAG(true);
    handleClose(true);
  }

  function handleAcceptSelected() {
    setPreferencesStorage(preferences ? "true" : "false");
    setAnalyticsStorage(analytics ? "true" : "false");
    updateGTAG(analytics);

    handleClose(true);
  }

  function handleRefuseAll() {
    setPreferencesStorage("false");
    setAnalyticsStorage("false");
    updateGTAG(false);

    handleClose(true);
  }

  useEffect(() => {
    if (open) {
      setSeenCookieSettings("true");
      setPreferences(getStorageValue("cookiesPreferences", "false") === "true");
      setAnalytics(getStorageValue("cookiesAnalytics", "false") === "true");
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      scroll={"paper"}
      sx={{
        "& .MuiDialog-container": {
          alignItems: { xs: "flex-end", sm: "center" },
        },
        "& .MuiPaper-root": {
          width: { xs: "100%", sm: "606px" },
          margin: { xs: "0", sm: "32px" },
          padding: { xs: "24px 20px", sm: "32px 24px" },
          maxHeight: { xs: "85vh", sm: "calc(100% - 64px)" },
          borderRadius: { xs: "16px 16px 0 0", sm: "8px" },
        },
      }}
      aria-labelledby="cookie-settings-dialog-title"
      aria-describedby="cookie-settings-dialog-description"
    >
      <DialogTitle component="div" sx={{ padding: "0" }}>
        <Typography
          id="cookie-settings-dialog-title"
          variant="h4"
          className="red-bar"
          pt="12px"
        >
          Cookie settings
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ padding: 0, my: { xs: "20px", sm: "32px" } }}>
        <Stack spacing={"18px"}>
          <Stack>
            <Typography
              id="cookie-settings-dialog-description"
              variant="bodySmallWeightRegular"
            >
              Our website uses cookies which are necessary for running the
              website and for providing the services you request. We would also
              like to set the following optional cookies on your device. You can
              change these settings any time later by clicking "Manage cookies"
              at the bottom of any page. For more information, please read our{" "}
              <Link
                sx={{ textDecorationColor: "white", color: "blue" }}
                href={`https://new.abb.com/privacy-policy/en`}
                target="_blank"
                rel="noreferrer"
              >
                Privacy notice.
              </Link>
            </Typography>
            <Typography variant="bodySmallWeightRegular" mt={"18px"}>
              Please select the optional cookies we can set on your device:
            </Typography>
          </Stack>

          <CheckItem
            checked={analytics}
            onChange={() => setAnalytics((prev) => !prev)}
            label="Analytics"
            description="We collect statistics in order to understand how our visitors interact with the website and how we can improve it. The cookies collect information in a way that does not directly identify anyone."
          />
          <CheckItem
            checked={preferences}
            onChange={() => setPreferences((prev) => !prev)}
            label="Preferences"
            description="We store choices you have made so that they are remembered when you visit our website again in order to provide you a more personalized experience."
          />
        </Stack>
      </DialogContent>
      <DialogActions 
        sx={{ padding: 0 }}
      >
        <Stack 
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          width="100%"
          justifyContent="flex-end"
        >
          <Button variant="outlined" onClick={handleAcceptSelected} fullWidth={isMobile}>
            Accept selected
          </Button>
          <Button variant="outlined" onClick={handleRefuseAll} fullWidth={isMobile}>
            Decline all
          </Button>
          <Button
            variant="containedAbb"
            onClick={handleAcceptAll}
            fullWidth={isMobile}
            sx={{ "&:hover": { background: "#c62828" } }}
          >
            Accept all
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default ManageCookiesDialog;
